import React, { useEffect, useState } from 'react';
import { useFormik, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Card, CardBody, Col, FormGroup, Label, Row } from 'reactstrap';
import { addDays, addMonths } from 'date-fns';
import { formatDate } from 'helpers/formatDate';
import { showToastError } from 'helpers/utils/toast';
import { useOrderAlert } from 'hooks/useOrderAlert';
import { getProfileState } from 'components/CommonForBoth/TopbarDropdown/ProfileMenu';
import { typeColor } from 'helpers/styles/typeColor';
import { DATE_FORMAT_WITH_SYMBOL } from 'common/data/constants';
import { USER_SEX } from 'common/data/constants';
import {
  isUserClient,
  isUserEmployee,
  isUserManager,
  isUserIndividual,
  isUserOperator,
} from 'helpers/jwt_helper';
import {
  StyledButtonCancel,
  StyledButtonSave,
} from 'components/Common/StyledButtonSave';
import { SelectApplicant } from '../OrderEditor/SelectApplicant';
import { SelectCountry } from 'components/SelectCountry/SelectCountry';
import { StyledDatePicker } from 'components/CustomDatepicker';
import { StyledInput } from 'components/Common/FormikInput';
import { StyledPhoneInput } from 'components/Common';
import { Select } from 'components/Common';
import { PassportScanner } from 'components/PassportScanner/PassportScanner';
import { usePassportScanner } from 'hooks/usePassportScanner';
import {
  APPLICANT_ORDER_FORM_VALIDATOR,
  APPLICANT_WITH_BIRTHDATE_ORDER_FORM_VALIDATOR,
  APPLICANT_WITH_PASSPORT_ORDER_FORM_VALIDATOR,
} from 'forms/validators';
import {
  operatorOrganizationAddApplicants,
  operatorUserAddApplicants,
  changeApplicant,
  getApplicant,
} from 'helpers/api-requests/admin';
import {
  createApplicant,
  fetchApplicant,
  putApplicant,
} from 'helpers/api-requests/manager';
import { fetchEmployee, putEmployee } from 'helpers/api-requests/employee';
import {
  convertApplicantFromApi,
  convertApplicantBasicDataToApi,
  convertApplicantWithBirthdateToApi,
  convertApplicantToApi,
  convertDateToAPI,
  convertPassportFromApi,
} from 'helpers/converters';
import * as Yup from 'yup';
import { Toggle } from 'components/Common/Toggle';
import HelpText from 'components/HelpText';

const dateFeatureSixMouth = addMonths(new Date(), 6);

const ApplicantEditForm = ({
  applicant,
  handleClose,
  index,
  removeApplicant,
  orderTypeFieldsSettings,
}) => {
  const { t } = useTranslation();
  const formikOrder = useFormikContext();
  const profile = useSelector(getProfileState);
  const { openAlert } = useOrderAlert();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [applicantInitEmail, setApplicantInitEmail] = useState();

  // New applicants have no isSimplifiedMode (undefined) → default to showing the toggle.
  const showToggle =
    isUserOperator &&
    orderTypeFieldsSettings?.enableApplicantPassport &&
    (applicant.isSimplifiedMode ?? true);

  // Initialize toggle from applicant's isSimplifiedMode flag
  const [useSimplifiedForm, setUseSimplifiedForm] = useState(
    orderTypeFieldsSettings?.enableApplicantPassport && isUserOperator
      ? !!applicant.id && !!applicant.isSimplifiedMode
        ? true
        : false
      : false,
  );

  const isDisabledFields = isUserEmployee && !applicant?.id;
  const hasPassport = orderTypeFieldsSettings?.enableApplicantPassport;
  const hasBirthdate = orderTypeFieldsSettings?.enableApplicantBirthdate;

  // Get validation schema
  const getValidationSchema = () => {
    if (useSimplifiedForm) {
      return Yup.object().shape({
        firstName: Yup.string().required(t('validation.required')),
        lastName: Yup.string().required(t('validation.required')),
        email: Yup.string()
          .email(t('validation.invalidEmail'))
          .required(t('validation.required')),
        additionalName: Yup.string().nullable(),
        sex: Yup.string().nullable(),
        phone: Yup.string().nullable(),
        birthday: Yup.string().nullable(),
        passport: Yup.object().shape({
          nationality: Yup.string().nullable(),
          number: Yup.string().nullable(),
          startDate: Yup.string().nullable(),
          expirationDate: Yup.string().nullable(),
        }),
      });
    }

    if (orderTypeFieldsSettings?.enableApplicantPassport) {
      return APPLICANT_WITH_PASSPORT_ORDER_FORM_VALIDATOR;
    }
    if (orderTypeFieldsSettings?.enableApplicantBirthdate) {
      return APPLICANT_WITH_BIRTHDATE_ORDER_FORM_VALIDATOR;
    }
    return APPLICANT_ORDER_FORM_VALIDATOR;
  };

  const applicantToApiConverter = values => {
    if (useSimplifiedForm) {
      return convertApplicantBasicDataToApi(values);
    }

    if (orderTypeFieldsSettings?.enableApplicantPassport) {
      return convertApplicantToApi(values);
    } else if (orderTypeFieldsSettings?.enableApplicantBirthdate) {
      return convertApplicantWithBirthdateToApi(values);
    } else {
      return convertApplicantBasicDataToApi(values);
    }
  };

  const showFieldError = fieldName => {
    let error = formik.errors;
    let touched = formik.touched;
    let value = formik.values;

    if (fieldName.includes('.')) {
      const pathParts = fieldName.split('.');
      for (const part of pathParts) {
        error = error?.[part];
        touched = touched?.[part];
        value = value?.[part];
        if (error === undefined && touched === undefined) break;
      }
    } else {
      error = formik.errors[fieldName];
      touched = formik.touched[fieldName];
      value = formik.values[fieldName];
    }

    const hasError =
      touched &&
      Boolean(error) &&
      (value === null ||
        value === undefined ||
        (typeof value === 'string' && value.trim() === '') ||
        (typeof value === 'object' && !value) ||
        Boolean(error));

    return hasError;
  };

  const handleSave = async values => {
    let isMounted = true;

    setIsSubmitting(true);
    const applicantsId =
      formikOrder.values.applicants?.map(
        applicant => applicant?.applicantDetail?.id,
      ) || [];

    if (applicant?.id !== values.id && applicantsId.includes(values.id)) {
      openAlert({
        message: `order.existingApplicantNotification`,
        type: typeColor.danger,
      });
      if (isMounted) setIsSubmitting(false);
      return;
    }

    const applicantCard = document.getElementById(`applicant-${index + 1}`);

    try {
      if (!values.id) {
        let response;

        const requestData = {
          ...applicantToApiConverter(values),
          is_simplified_mode: useSimplifiedForm,
        };

        if (isUserClient) {
          response = await createApplicant(requestData);
        }

        if (!isUserClient) {
          response =
            formikOrder.values.client?.clientType === 'legal'
              ? await operatorOrganizationAddApplicants(
                  formikOrder.values.client?.client,
                  requestData,
                )
              : await operatorUserAddApplicants(
                  formikOrder.values.client?.client,
                  requestData,
                );
        }

        if (response?.success) {
          formikOrder.setFieldValue(
            `applicants[${index}].statusApplicant`,
            useSimplifiedForm ? 'request_sent' : 'draft',
          );

          formikOrder.setFieldValue(`applicants[${index}].applicantDetail`, {
            ...values,
            citizenship: values.passport?.nationality ?? null,
            requireResidency:
              response.data?.passports?.[0]?.country
                ?.visa_order_require_residency,
            additionalName: values.additionalName ?? undefined,
            id: response.data?.id,
            isSimplifiedMode: useSimplifiedForm,
          });

          applicantCard?.scrollIntoView(false);
          if (isMounted) setIsSubmitting(false);
          handleClose();
          return;
        } else {
          openAlert({
            message: `${t('passport.notValidDate')} "${t(
              'passport.minimumDate',
            )} ${formatDate(
              addDays(dateFeatureSixMouth, 1),
              DATE_FORMAT_WITH_SYMBOL,
            )}"`,
            type: typeColor.danger,
          });
          if (isMounted) setIsSubmitting(false);
          return;
        }
      }

      // For existing applicant updates
      const updateData = {
        ...applicantToApiConverter(values),
        is_simplified_mode: useSimplifiedForm,
      };

      let currentRequest = changeApplicant(updateData, values.id);

      if (isUserClient) {
        currentRequest = putApplicant({
          applicant: updateData,
          id: values.id,
        });
      }

      if (isUserEmployee) {
        currentRequest = putEmployee({
          applicant: updateData,
        });
      }

      currentRequest
        .then(response => {
          if (!isMounted) return;

          if (response?.success) {
            formikOrder.setFieldValue(
              `applicants[${index}].statusApplicant`,
              useSimplifiedForm ? 'request_sent' : 'draft',
            );

            if (
              orderTypeFieldsSettings?.enableApplicantPassport &&
              !useSimplifiedForm
            ) {
              if (
                !response.data?.passports?.find(
                  passport =>
                    passport.number === values.passport?.number &&
                    passport.end_date ===
                      convertDateToAPI(values.passport?.expirationDate) &&
                    passport.issue_date ===
                      convertDateToAPI(values.passport?.startDate),
                )?.validity?.is_valid
              ) {
                openAlert({
                  message: `${t('passport.notValidDate')} "${t(
                    'passport.minimumDate',
                  )} ${formatDate(
                    addDays(dateFeatureSixMouth, 1),
                    DATE_FORMAT_WITH_SYMBOL,
                  )}"`,
                  type: typeColor.danger,
                });
                setIsSubmitting(false);
                return;
              }
            }

            formikOrder.setFieldValue(`applicants[${index}].applicantDetail`, {
              ...values,
              citizenship: values.passport?.nationality,
              additionalName: values.additionalName ?? undefined,
              isSimplifiedMode: useSimplifiedForm,
            });
            applicantCard?.scrollIntoView(false);
            setIsSubmitting(false);
            handleClose();
            return;
          }
          showToastError(response?.message);
          setIsSubmitting(false);
        })
        .catch(err => {
          if (!isMounted) return;
          showToastError(err ?? 'Error while editing the applicant');
          setIsSubmitting(false);
        });
    } catch (error) {
      if (!isMounted) return;
      console.log(error);
      setIsSubmitting(false);
    }
  };

  const formik = useFormik({
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    initialValues: applicant,
    validationSchema: getValidationSchema(),
    onSubmit: handleSave,
  });

  // Use the passport scanner hook
  const {
    isLoading: isScanning,
    handleScanPassport,
    scanError,
    clearScanError,
    scanSuccess,
    clearScanSuccess,
  } = usePassportScanner({
    onScanComplete: data => {
      if (data?.first_name) {
        formik.setFieldValue('firstName', data.first_name);
      }
      if (data?.last_name) {
        formik.setFieldValue('lastName', data.last_name);
      }
      if (data?.date_of_birth) {
        formik.setFieldValue('birthday', new Date(data.date_of_birth));
      }

      const sex_val = USER_SEX.find(({ value }) => value === data?.sex);
      if (sex_val?.value) {
        formik.setFieldValue('sex', sex_val.value);
      }

      if (data?.country?.id) {
        formik.setFieldValue('passport.nationality', data.country.id);
      }
      if (data?.number) {
        formik.setFieldValue('passport.number', data.number);
      }
      if (data?.issuance_date) {
        formik.setFieldValue(
          'passport.startDate',
          new Date(data.issuance_date),
        );
      }
      if (data?.expiration_date) {
        formik.setFieldValue(
          'passport.expirationDate',
          new Date(data.expiration_date),
        );
      }
    },
  });

  useEffect(() => {
    if (isUserIndividual && profile?.email) {
      formik.setFieldValue('email', profile.email);
    }
  }, []);

  const getApplicantWithPassports = () => {
    if (!applicant?.id) return;

    const currentRequest = isUserClient
      ? fetchApplicant(applicant.id)
      : getApplicant(applicant.id);

    currentRequest
      .then(response => {
        if (response?.success) {
          if (
            !response.data?.passports ||
            response.data.passports.length === 0
          ) {
            console.log('No passport data in response, keeping current values');
            return;
          }

          const passportFormApi = convertPassportFromApi(
            response.data?.passports?.find(
              passport => passport.number === applicant?.passport?.number,
            ),
          );

          if (passportFormApi) {
            formik.setValues({
              ...applicant,
              citizenship: passportFormApi?.nationality,
              passport: {
                ...passportFormApi,
              },
              passports: response.data?.passports,
            });
          }
        } else {
          showToastError(response?.message);
        }
      })
      .catch(err => console.log(err));
  };

  const getApplicantEmployee = () => {
    fetchEmployee()
      .then(response => {
        if (response?.success) {
          if (
            !response.data?.passports ||
            response.data.passports.length === 0
          ) {
            console.log(
              'No passport data in employee response, keeping current values',
            );
            return;
          }

          const passportFormApi = response.data?.passport
            ? convertPassportFromApi(
                response.data?.passports?.find(
                  passport => passport.number === applicant?.passport?.number,
                ),
              )
            : {};

          if (passportFormApi) {
            formik.setValues({
              ...applicant,
              citizenship: passportFormApi?.nationality,
              passport: {
                ...passportFormApi,
              },
              passports: response.data?.passports,
            });
          }
        } else {
          showToastError(response?.message);
        }
      })
      .catch(err => console.log(err));
  };

  useEffect(() => {
    if (isUserEmployee && applicant?.id) {
      getApplicantEmployee();
      return;
    }

    if (applicant?.id) {
      getApplicantWithPassports();
    }
  }, [applicant?.id]);

  const handleSetApplicant = applicantFromApi => {
    setApplicantInitEmail();
    if (applicantFromApi) {
      const convertedData = convertApplicantFromApi(applicantFromApi);
      formik.setValues(convertedData);
      formik.setFieldValue('name', 'New Name');

      if (convertedData.email) {
        setApplicantInitEmail(convertedData.email);
      }
    } else {
      formik.setFieldValue('firstName', null);
    }
  };

  useEffect(() => {
    if (formik.values?.id && formik.values?.email && !applicantInitEmail) {
      setApplicantInitEmail(formik.values?.email);
    }
  }, [formik.values]);

  const setManagerEmail = () =>
    formik.setFieldValue('email', profile?.email || '');
  const setApplicantEmail = () =>
    formik.setFieldValue('email', applicantInitEmail || '');

  const renderEmailActions = () => {
    if (!isUserManager || !profile?.email) return null;

    const isManagerEmailActive = formik.values?.email === profile?.email;
    const targetEmail = isManagerEmailActive
      ? applicantInitEmail
      : profile?.email;

    if (!targetEmail) return null;

    const handleClick = isManagerEmailActive
      ? setApplicantEmail
      : setManagerEmail;

    const label = isManagerEmailActive
      ? "Use applicant's email"
      : 'Use my email';

    const subLabel = isManagerEmailActive ? 'Applicant email' : 'Manager email';

    return (
      <div
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && handleClick()}
        title={targetEmail}
        style={{
          display: 'flex',
          width: '100%',
          boxSizing: 'border-box',
          alignItems: 'center',
          gap: '10px',
          marginTop: '6px',
          padding: '8px 12px',
          fontSize: '.85rem',
          color: '#0d6efd',
          background: '#f0f6ff',
          border: '1px dashed #0d6efd',
          borderRadius: '8px',
          cursor: 'pointer',
          transition: 'background 0.2s ease',
        }}
        onMouseEnter={e => (e.currentTarget.style.background = '#e2edff')}
        onMouseLeave={e => (e.currentTarget.style.background = '#f0f6ff')}
      >
        <i
          className="bx bx-paste"
          style={{ flexShrink: 0, fontSize: '1rem' }}
        />
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'baseline',
            gap: '6px',
            minWidth: 0,
            flex: 1,
          }}
        >
          <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{label}</span>
          <span
            style={{
              fontSize: '.72rem',
              color: '#6c757d',
              wordBreak: 'break-all',
            }}
          >
            ({subLabel}: {targetEmail})
          </span>
        </div>
      </div>
    );
  };

  const renderPassportFields = () => {
    if (!hasPassport || useSimplifiedForm) return null;

    return (
      <>
        <FormGroup row className="align-items-center mb-3">
          <Col sm={12} xxl={3}>
            <Label className="fw-semibold text-secondary">
              <i className="bx bx-flag me-1"></i>
              {t('passport.nationality')}
              <span className="text-danger ms-1">*</span>
            </Label>
          </Col>
          <Col sm={12} xxl={9}>
            <SelectCountry
              isAvailableCitizenship
              onBlur={() => {
                formik.setFieldTouched('passport.nationality', true);
              }}
              onChange={option => {
                formik.setFieldValue(
                  'passport.nationality',
                  option?.value ?? null,
                );
                formik.setFieldTouched('passport.nationality', true);
              }}
              isDisabled={isDisabledFields}
              invalid={showFieldError('passport.nationality')}
              value={formik.values?.passport?.nationality}
              className="w-100"
              placeholder={t('passport.nationalityPlaceholder')}
            />
            {showFieldError('passport.nationality') && (
              <div className="text-danger small mt-1">
                <i className="bx bx-error-circle me-1"></i>
                {t('validation.required')}
              </div>
            )}
          </Col>
        </FormGroup>

        <FormGroup row className="align-items-center mb-3">
          <Col sm={12} xxl={3}>
            <Label className="fw-semibold text-secondary">
              <i className="bx bx-hash me-1"></i>
              {t('passport.number')}
              <span className="text-danger ms-1">*</span>
            </Label>
          </Col>
          <Col sm={12} xxl={9}>
            <StyledInput
              className="w-100 p-2 ps-3"
              name="passport.number"
              onBlur={formik.handleBlur}
              onChange={e => {
                formik.handleChange(e);
              }}
              value={formik.values?.passport?.number || ''}
              placeholder={t('passport.numberPlaceholder')}
              invalid={showFieldError('passport.number')}
              disabled={isDisabledFields}
            />
            {showFieldError('passport.number') && (
              <div className="text-danger small mt-1">
                <i className="bx bx-error-circle me-1"></i>
                {t('validation.required')}
              </div>
            )}
          </Col>
        </FormGroup>

        {/* Start date — own row */}
        <FormGroup row className="align-items-center mb-3">
          <Col sm={12} xxl={3}>
            <Label className="fw-semibold text-secondary">
              <i className="bx bx-calendar-check me-1"></i>
              {t('passport.startDate')}
              <span className="text-danger ms-1">*</span>
            </Label>
          </Col>
          <Col sm={12} xxl={9}>
            <StyledDatePicker
              onBlur={() => formik.setFieldTouched('passport.startDate', true)}
              onChange={option => {
                formik.setFieldValue('passport.startDate', option);
                formik.setFieldTouched('passport.startDate', true);
              }}
              selected={formik.values?.passport?.startDate || null}
              invalid={showFieldError('passport.startDate')}
              maxDate={new Date()}
              className="w-100 p-2 ps-3"
              placeholderText={t('common.datePlaceholder')}
              selectsStart
              disabled={isDisabledFields}
              startDate={formik.values?.passport?.startDate}
              endDate={formik.values?.passport?.expirationDate}
            />
            {showFieldError('passport.startDate') && (
              <div className="text-danger small mt-1">
                <i className="bx bx-error-circle me-1"></i>
                {t('validation.required')}
              </div>
            )}
          </Col>
        </FormGroup>

        {/* Expiration date — own row */}
        <FormGroup row className="align-items-center mb-3">
          <Col sm={12} xxl={3}>
            <Label className="fw-semibold text-secondary">
              <i className="bx bx-calendar-x me-1"></i>
              {t('passport.expirationDate')}
              <span className="text-danger ms-1">*</span>
            </Label>
          </Col>
          <Col sm={12} xxl={9}>
            <StyledDatePicker
              onBlur={() =>
                formik.setFieldTouched('passport.expirationDate', true)
              }
              onChange={option => {
                formik.setFieldValue('passport.expirationDate', option);
                formik.setFieldTouched('passport.expirationDate', true);
              }}
              selected={formik.values?.passport?.expirationDate || null}
              minDate={new Date()}
              invalid={showFieldError('passport.expirationDate')}
              className="w-100 p-2 ps-3"
              placeholderText={t('common.datePlaceholder')}
              selectsEnd
              disabled={isDisabledFields}
              startDate={formik.values?.passport?.startDate}
              endDate={formik.values?.passport?.expirationDate}
            />
            {showFieldError('passport.expirationDate') && (
              <div className="text-danger small mt-1">
                <i className="bx bx-error-circle me-1"></i>
                {t('validation.required')}
              </div>
            )}
          </Col>
        </FormGroup>
      </>
    );
  };

  const renderBasicFields = () => (
    <>
      <FormGroup row className="align-items-center mb-3">
        <Col sm={12} xxl={3}>
          <Label className="fw-semibold text-secondary">
            <i className="bx bx-user me-1"></i>
            {t('auth.firstName')}
            <span className="text-danger ms-1">*</span>
          </Label>
        </Col>
        <Col sm={12} xxl={9}>
          {!applicant?.id && !useSimplifiedForm ? (
            <SelectApplicant
              classNamePrefix="react-select"
              className="w-100"
              handleSelectApplicant={handleSetApplicant}
              clientType={formikOrder.values.client?.clientType}
              clientId={formikOrder.values.client?.client}
              value={formik.values?.firstName}
              onBlur={() => {
                formik.setFieldTouched('firstName', true);
              }}
              handleChangeTextInput={event => {
                if (!event) {
                  formik.setFieldValue('firstName', null);
                } else {
                  formik.setFieldValue('firstName', event);
                }
                formik.setFieldTouched('firstName', true);
              }}
              selectedApplicants={formikOrder.values?.applicants}
              placeholder={t('order.applicantPlaceholder')}
              invalid={showFieldError('firstName')}
            />
          ) : (
            <StyledInput
              name="firstName"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              className="p-2 ps-3"
              value={formik.values?.firstName || ''}
              placeholder={t('auth.enterFirstName')}
              disabled={isDisabledFields}
              invalid={showFieldError('firstName')}
            />
          )}
          {showFieldError('firstName') && (
            <div className="text-danger small mt-1">
              <i className="bx bx-error-circle me-1"></i>
              {t('validation.required')}
            </div>
          )}
        </Col>
      </FormGroup>

      <FormGroup row className="align-items-center mb-3">
        <Col sm={12} xxl={3}>
          <Label className="fw-semibold text-secondary">
            <i className="bx bx-user me-1"></i>
            {t('auth.lastName')}
            <span className="text-danger ms-1">*</span>
          </Label>
        </Col>
        <Col sm={12} xxl={9}>
          <StyledInput
            name="lastName"
            onBlur={formik.handleBlur}
            onChange={e => {
              formik.handleChange(e);
            }}
            className="p-2 ps-3"
            value={formik.values?.lastName || ''}
            placeholder={t('auth.enterLastName')}
            disabled={isDisabledFields}
            invalid={showFieldError('lastName')}
          />
          {showFieldError('lastName') && (
            <div className="text-danger small mt-1">
              <i className="bx bx-error-circle me-1"></i>
              {t('validation.required')}
            </div>
          )}
        </Col>
      </FormGroup>

      <FormGroup row className="align-items-center mb-3">
        <Col sm={12} xxl={3}>
          <Label className="fw-semibold text-secondary">
            <i className="bx bx-user-plus me-1"></i>
            {t('auth.additionalName')}
          </Label>
        </Col>
        <Col sm={12} xxl={9}>
          <StyledInput
            className="p-2 ps-3"
            name="additionalName"
            value={formik.values?.additionalName || ''}
            disabled={isDisabledFields}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            placeholder={t('auth.enterAdditionalName')}
          />
        </Col>
      </FormGroup>

      <FormGroup row className="align-items-center mb-3">
        <Col sm={12} xxl={3}>
          <Label className="fw-semibold text-secondary">
            <i className="bx bx-male-female me-1"></i>
            {t('common.gender')}
            {!useSimplifiedForm && <span className="text-danger ms-1">*</span>}
          </Label>
        </Col>
        <Col sm={12} xxl={9}>
          <Select
            placeholder={t('common.genderPlaceholder')}
            isDisabled={isDisabledFields}
            classNamePrefix="react-select"
            value={
              USER_SEX.find(({ value }) => value === formik.values.sex) ?? null
            }
            onChange={option => {
              formik.setFieldValue('sex', option?.value ?? null);
              formik.setFieldTouched('sex', true);
            }}
            options={USER_SEX}
            invalid={showFieldError('sex')}
          />
          {showFieldError('sex') && (
            <div className="text-danger small mt-1">
              <i className="bx bx-error-circle me-1"></i>
              {t('validation.required')}
            </div>
          )}
        </Col>
      </FormGroup>

      <FormGroup row className="align-items-center mb-3">
        <Col sm={12} xxl={3}>
          <Label className="fw-semibold text-secondary">
            <i className="bx bx-phone me-1"></i>
            {t('common.phone')}
            {!useSimplifiedForm && <span className="text-danger ms-1">*</span>}
          </Label>
        </Col>
        <Col sm={12} xxl={9}>
          <StyledPhoneInput
            disabled={isDisabledFields}
            onBlur={() => {
              formik.setFieldTouched('phone', true);
            }}
            onChange={phone => {
              formik.setFieldValue('phone', phone);
              formik.setFieldTouched('phone', true);
            }}
            value={formik.values?.phone || ''}
            placeholder={t('common.phonePlaceholder')}
          />
          {formik.touched.phone && formik.errors.phone && (
            <div className="text-danger small mt-1 d-flex align-items-center gap-1">
              <i className="bx bx-error-circle" />
              {formik.errors.phone}
            </div>
          )}
        </Col>
      </FormGroup>

      <FormGroup row className="mb-3">
        <Col sm={12} xxl={3}>
          <Label className="fw-semibold text-secondary">
            <i className="bx bx-envelope me-1"></i>
            {t('auth.email')}
            <span className="text-danger ms-1">*</span>
          </Label>
        </Col>
        <Col sm={12} xxl={9}>
          <StyledInput
            name="email"
            onBlur={formik.handleBlur}
            onChange={e => {
              formik.handleChange(e);
            }}
            className="p-2 ps-3 w-100"
            disabled={
              isDisabledFields ||
              isUserIndividual ||
              isUserEmployee ||
              (isUserManager && formik.values?.id && formik.values?.email)
            }
            value={formik.values?.email || ''}
            placeholder={t('auth.enterEmail')}
            invalid={showFieldError('email')}
          />
          {renderEmailActions()}
          {showFieldError('email') && (
            <div className="text-danger small mt-1">
              <i className="bx bx-error-circle me-1"></i>
              {t('validation.required')}
            </div>
          )}
        </Col>
      </FormGroup>

      {hasBirthdate && !useSimplifiedForm && (
        <FormGroup row className="align-items-center mb-3">
          <Col sm={12} xxl={3}>
            <Label className="fw-semibold text-secondary">
              <i className="bx bx-calendar me-1"></i>
              {t('auth.birthday')}
              <span className="text-danger ms-1">*</span>
            </Label>
          </Col>
          <Col sm={12} xxl={9}>
            <StyledDatePicker
              onBlur={() => {
                formik.setFieldTouched('birthday', true);
              }}
              onChange={option => {
                formik.setFieldValue('birthday', option);
                formik.setFieldTouched('birthday', true);
              }}
              disabled={isDisabledFields}
              selected={formik.values?.birthday || null}
              maxDate={new Date()}
              className="w-100 p-2 ps-3"
              placeholderText={t('auth.birthdayPlaceholder')}
              invalid={showFieldError('birthday')}
            />
            {showFieldError('birthday') && (
              <div className="text-danger small mt-1">
                <i className="bx bx-error-circle me-1"></i>
                {t('validation.required')}
              </div>
            )}
          </Col>
        </FormGroup>
      )}
    </>
  );

  const handleSubmitButtonClick = async e => {
    e.preventDefault();

    const errors = await formik.validateForm();

    const isEmpty = value => {
      return (
        value === null ||
        value === undefined ||
        (typeof value === 'string' && value.trim() === '') ||
        (typeof value === 'object' && !value)
      );
    };

    let hasErrors = false;

    ['firstName', 'lastName', 'email'].forEach(field => {
      const value = formik.values[field];
      if (errors[field] && isEmpty(value)) {
        hasErrors = true;
      }
    });

    if (!useSimplifiedForm) {
      ['sex', 'phone'].forEach(field => {
        const value = formik.values[field];
        if (errors[field] && isEmpty(value)) {
          hasErrors = true;
        }
      });

      if (hasBirthdate) {
        if (errors.birthday && isEmpty(formik.values.birthday)) {
          hasErrors = true;
        }
      }

      if (hasPassport) {
        const passportFields = [
          'nationality',
          'number',
          'startDate',
          'expirationDate',
        ];
        passportFields.forEach(field => {
          const fieldError = errors.passport?.[field];
          const fieldValue = formik.values.passport?.[field];
          if (fieldError && isEmpty(fieldValue)) {
            hasErrors = true;
          }
        });
      }
    }

    if (hasErrors) {
      const touchedFields = {
        firstName: true,
        lastName: true,
        email: true,
      };

      if (!useSimplifiedForm) {
        touchedFields.sex = true;
        touchedFields.phone = true;

        if (hasBirthdate) {
          touchedFields.birthday = true;
        }

        if (hasPassport) {
          touchedFields.passport = {
            nationality: true,
            number: true,
            startDate: true,
            expirationDate: true,
          };
        }
      }

      formik.setTouched(touchedFields);
      formik.setErrors(errors);
      return;
    }

    formik.handleSubmit(e);
  };

  const handleCancel = () => {
    // Reset toggle to the applicant's actual isSimplifiedMode state
    setUseSimplifiedForm(applicant?.isSimplifiedMode || false);
    if (!formikOrder.values?.applicants?.[index]?.applicantDetail?.firstName) {
      removeApplicant();
    } else {
      handleClose();
    }
  };

  const handleToggleChange = () => {
    setUseSimplifiedForm(!useSimplifiedForm);
  };

  return (
    <Card className="border-0 shadow-sm w-100">
      <CardBody className="p-1">
        {/* Toggle for operators */}
        {showToggle && (
          <div className="mb-3 p-3 bg-light rounded-3">
            <Toggle
              checked={useSimplifiedForm}
              onChange={handleToggleChange}
              label={t('order.simplifiedFormMode')}
            />
            <HelpText variant="info" noBackground hideIcon>
              {t('order.simplifiedFormDescription')}
            </HelpText>
            {useSimplifiedForm !== applicant?.isSimplifiedMode && (
              <div className="text-warning small mt-1">
                <i className="bx bx-info-circle me-1"></i>
                {t('order.changesNotSaved', {
                  defaultValue: 'Changes will be applied when you save.',
                })}
              </div>
            )}
          </div>
        )}

        <form onSubmit={formik.handleSubmit}>
          <Row className="align-items-stretch">
            <Col
              xs={12}
              md={hasPassport && !useSimplifiedForm ? 7 : 12}
              className="d-flex flex-column"
            >
              <div className="bg-light p-3 rounded-3 mb-3 flex-grow-1">
                <h6 className="text-muted mb-3">
                  <i className="bx bx-user me-2"></i>
                  {t('order.personalInformation')}
                </h6>
                {renderBasicFields()}
              </div>

              {hasPassport && !useSimplifiedForm && (
                <div className="bg-light p-3 rounded-3 mb-3">
                  <h6 className="text-muted mb-3">
                    <i className="bx bx-id-card me-2"></i>
                    {t('passport.information')}
                  </h6>
                  {renderPassportFields()}
                </div>
              )}

              <FormGroup row className="mt-auto">
                <Col sm={12} xxl={2}></Col>
                <Col sm={12} xxl={6}>
                  <div className="d-flex gap-3">
                    <StyledButtonSave
                      className="flex-1"
                      type="button"
                      onClick={handleSubmitButtonClick}
                      disabled={isSubmitting}
                      color="primary"
                    >
                      <i className="bx bx-save me-1"></i>
                      {t('common.submit')}
                    </StyledButtonSave>

                    <StyledButtonCancel
                      className="flex-1"
                      type="button"
                      onClick={handleCancel}
                    >
                      <i className="bx bx-x me-1"></i>
                      {t('common.cancel')}
                    </StyledButtonCancel>
                  </div>
                </Col>
              </FormGroup>
            </Col>

            {hasPassport && !useSimplifiedForm && (
              <Col xs={12} md={5} className="d-flex align-items-start">
                <div className="w-100" style={{ top: '20px' }}>
                  <div className="bg-light p-3 rounded-3 h-100 d-flex flex-column">
                    <div className="flex-grow-1 d-flex align-items-center">
                      <PassportScanner
                        onScan={handleScanPassport}
                        isLoading={isScanning}
                        error={scanError}
                        onClearError={clearScanError}
                        success={scanSuccess}
                        onClearSuccess={clearScanSuccess}
                      />
                    </div>
                  </div>
                </div>
              </Col>
            )}
          </Row>
        </form>
      </CardBody>
    </Card>
  );
};

export default ApplicantEditForm;
